import { useState, useEffect } from "react";
import { Typography } from "@material-tailwind/react";
import {
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  ClockIcon,
  MagnifyingGlassCircleIcon,
  MinusCircleIcon,
  NoSymbolIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { db } from "../../firebase";
import { DateTime } from "luxon";
import { Timestamp } from "firebase/firestore";

export type PhoneConfigHistoryEntry = {
  id: string;
  timestamp?: Timestamp | Date | string;
  status?: string;
  previousStatus?: string;
  reason?: string;
  changedBy?: string;
  notes?: string;
  [key: string]: any;
};

type StatusConfig = {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const STATUS_CONFIG: Record<string, StatusConfig> = {
  verified: {
    label: "Verified",
    color: "text-green-700",
    bgColor: "bg-green-100",
    borderColor: "border-green-400",
    Icon: CheckCircleIcon,
  },
  failed: {
    label: "Failed",
    color: "text-red-700",
    bgColor: "bg-red-100",
    borderColor: "border-red-400",
    Icon: XCircleIcon,
  },
  submitted: {
    label: "Submitted",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    borderColor: "border-blue-400",
    Icon: PaperAirplaneIcon,
  },
  pending: {
    label: "Pending",
    color: "text-amber-700",
    bgColor: "bg-amber-100",
    borderColor: "border-amber-400",
    Icon: ClockIcon,
  },
  "in-review": {
    label: "In Review",
    color: "text-purple-700",
    bgColor: "bg-purple-100",
    borderColor: "border-purple-400",
    Icon: MagnifyingGlassCircleIcon,
  },
  unverified: {
    label: "Unverified",
    color: "text-blue-gray-600",
    bgColor: "bg-blue-gray-100",
    borderColor: "border-blue-gray-300",
    Icon: MinusCircleIcon,
  },
  ignore: {
    label: "Ignore",
    color: "text-gray-500",
    bgColor: "bg-gray-100",
    borderColor: "border-gray-300",
    Icon: NoSymbolIcon,
  },
};

const DEFAULT_STATUS_CONFIG: StatusConfig = {
  label: "Unknown",
  color: "text-blue-gray-500",
  bgColor: "bg-blue-gray-50",
  borderColor: "border-blue-gray-200",
  Icon: InformationCircleIcon,
};

const formatTimestamp = (timestamp?: Timestamp | Date | string): string => {
  if (!timestamp) return "—";
  try {
    let dt: DateTime;
    if (timestamp instanceof Date) {
      dt = DateTime.fromJSDate(timestamp);
    } else if (typeof timestamp === "string") {
      dt = DateTime.fromISO(timestamp);
    } else {
      // Firestore Timestamp (compat or modular)
      const secs = (timestamp as any).seconds ?? (timestamp as any)._seconds;
      const nanos =
        (timestamp as any).nanoseconds ?? (timestamp as any)._nanoseconds ?? 0;
      dt = DateTime.fromSeconds(secs + nanos / 1e9);
    }
    return dt.toFormat("MM/dd/yyyy h:mm a");
  } catch {
    return "—";
  }
};

const EXTRA_FIELD_LABELS: Record<string, string> = {
  previousStatus: "Previous Status",
  reason: "Reason",
  changedBy: "Changed By",
  notes: "Notes",
  email: "Email Sent",
  failedReason: "Failure Reason",
  declineReasonDescription: "Decline Description",
};

const EXCLUDED_FIELDS = new Set(["id", "timestamp", "status"]);

type TimelineItemProps = {
  entry: PhoneConfigHistoryEntry;
  isLast: boolean;
};

const TimelineItem = ({ entry, isLast }: TimelineItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const config = STATUS_CONFIG[entry.status ?? ""] ?? DEFAULT_STATUS_CONFIG;
  const { Icon, color, bgColor, borderColor } = config;

  const extraFields = Object.entries(entry).filter(
    ([key, val]) =>
      !EXCLUDED_FIELDS.has(key) &&
      val !== undefined &&
      val !== null &&
      val !== "",
  );

  const hasDetails = extraFields.length > 0;

  return (
    <div className="flex gap-3">
      {/* Left column: icon + vertical line */}
      <div className="flex flex-col items-center">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${bgColor} ${borderColor} shadow-sm`}
        >
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        {!isLast && <div className="mt-1 w-0.5 flex-1 bg-blue-gray-200" />}
      </div>

      {/* Right column: content */}
      <div className={`pb-6 ${isLast ? "" : ""}`}>
        <div
          className={`group relative cursor-pointer rounded-lg border px-4 py-3 shadow-sm transition-shadow hover:shadow-md ${bgColor} ${borderColor} ${
            hasDetails ? "cursor-pointer" : "cursor-default"
          }`}
          onClick={() => hasDetails && setExpanded((v) => !v)}
        >
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${color}`}>
              {config.label}
            </span>
            {hasDetails && (
              <span className="text-xs text-blue-gray-400">
                {expanded ? "▲" : "▼"}
              </span>
            )}
          </div>
          <Typography
            variant="small"
            className="mt-0.5 text-xs text-blue-gray-500"
          >
            {formatTimestamp(entry.timestamp)}
          </Typography>

          {/* Detail panel */}
          {hasDetails && expanded && (
            <div className="mt-3 border-t border-blue-gray-200 pt-3">
              <dl className="space-y-1">
                {extraFields.map(([key, value]) => {
                  const label = EXTRA_FIELD_LABELS[key] ?? key;
                  const displayValue =
                    typeof value === "boolean"
                      ? value
                        ? "Yes"
                        : "No"
                      : String(value);
                  return (
                    <div key={key} className="flex gap-2 text-xs">
                      <dt className="min-w-[100px] font-medium text-blue-gray-600">
                        {label}:
                      </dt>
                      <dd className="text-blue-gray-800">{displayValue}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type PhoneConfigTimelineProps = {
  accountId: string;
  refreshKey?: number;
};

export const PhoneConfigTimeline = ({
  accountId,
  refreshKey = 0,
}: PhoneConfigTimelineProps) => {
  const [history, setHistory] = useState<PhoneConfigHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) return;

    setIsLoading(true);
    setError(null);

    db.collection("accounts")
      .doc(accountId)
      .collection("phoneConfigHistory")
      .orderBy("timestamp", "asc")
      .get()
      .then((snapshot) => {
        const entries: PhoneConfigHistoryEntry[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setHistory(entries);
      })
      .catch((err) => {
        console.error("Error fetching phoneConfigHistory:", err);
        setError("Failed to load history.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [accountId, refreshKey]);

  if (isLoading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-blue-gray-400">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-gray-300 border-t-blue-500" />
        Loading history…
      </div>
    );
  }

  if (error) {
    return (
      <Typography variant="small" color="red" className="mt-2">
        {error}
      </Typography>
    );
  }

  if (history.length === 0) {
    return (
      <Typography variant="small" className="mt-2 italic text-blue-gray-400">
        No history available.
      </Typography>
    );
  }

  return (
    <div className="mt-2">
      {history.map((entry, index) => (
        <TimelineItem
          key={entry.id}
          entry={entry}
          isLast={index === history.length - 1}
        />
      ))}
    </div>
  );
};

export default PhoneConfigTimeline;
